import type { PreviewTemplate } from './preview';
import { SLOT_PACK_SCALE_KNOB } from './slot';
import type { AnimationVariant, OpenerVariant } from './types';
import { WEB_BASE } from './webBase';

export type KnobGroup =
  | 'General'
  | 'Pack'
  | 'Gesture'
  | 'Opening'
  | 'Reveal'
  | 'Charge'
  | 'Blast'
  | 'Card arrival'
  | 'Ring'
  | 'Story';

/** Order the console renders groups in. */
export const KNOB_GROUPS: readonly KnobGroup[] = [
  'General',
  'Pack',
  'Gesture',
  'Opening',
  'Reveal',
  'Charge',
  'Blast',
  'Card arrival',
  'Ring',
  'Story',
];

/** How a range knob's readout is written. */
export type KnobFormat = 'ms' | 'px' | 'x' | 'ratio' | 'count';

interface KnobBase {
  /** Dot path inside PackOpenerOptions, e.g. `motion.reveal.spinMs`. */
  path: string;
  /** Operator-facing label (English). */
  label: string;
  group: KnobGroup;
  /** Mechanics that read this knob. */
  variants: readonly AnimationVariant[];
  /** Templates that honour this knob; absent = every template. */
  templates?: readonly PreviewTemplate[];
  /**
   * Toggles this knob only works with: it is locked while any of them is off,
   * and, while it is a toggle that is on, they are locked on.
   */
  requires?: readonly string[];
}

export interface RangeKnob extends KnobBase {
  kind: 'range';
  min: number;
  max: number;
  step: number;
  format: KnobFormat;
  /** The preset's value — what the slider shows until the operator moves it. */
  defaultValue: number;
}

export interface ColorKnob extends KnobBase {
  kind: 'color';
  defaultValue: string;
}

export interface ToggleKnob extends KnobBase {
  kind: 'toggle';
  defaultValue: boolean;
}

/** A list of colours the operator can grow and shrink, between `min` and `max`. */
export interface ColorListKnob extends KnobBase {
  kind: 'colorList';
  min: number;
  max: number;
  defaultValue: readonly string[];
}

export type Knob = RangeKnob | ColorKnob | ToggleKnob | ColorListKnob;

/** What a knob writes at its path. */
export type KnobValue = number | string | boolean | readonly string[];

const ALL: readonly OpenerVariant[] = ['slice', 'burst', 'carousel'];
const SLICE: readonly OpenerVariant[] = ['slice'];
const BURST: readonly OpenerVariant[] = ['burst'];
const CAROUSEL: readonly OpenerVariant[] = ['carousel'];

const GLOW_WHILE_CHARGING = 'motion.reveal.glowDuring.charge';
const GLOW_WHILE_ART_LATE = 'motion.reveal.glowDuring.chargeHold';
const CHARGE_CYCLE = 'motion.reveal.chargeCycle';
/** The library's palette for the colour cycle (pack-opener-js ≥ 0.5.15). */
const CHARGE_CYCLE_COLORS: readonly string[] = ['#ffffff', '#97d926', '#0da7f2', '#8442f0'];
/** One colour is a steady neutral light; seven is every rarity tier. */
const CHARGE_CYCLE_MAX_COLORS = 7;

/**
 * The curated surface. Defaults are copied from the library presets (`classic`
 * for slice, `charged` for burst, `showcase` for carousel) — or from
 * `WEB_BASE` where the web host starts elsewhere; the General group has the
 * same value in all. Anything not listed here stays at the preset (or web
 * base) value.
 */
export const ANIMATION_KNOBS: readonly Knob[] = [
  // General — read by every mechanic
  { path: 'motion.speed', label: 'Speed', group: 'General', kind: 'range', min: 0.5, max: 2, step: 0.05, format: 'x', defaultValue: 1, variants: ALL },
  { path: 'theme.beam', label: 'Beam colour', group: 'General', kind: 'color', defaultValue: '#ffffff', variants: ['slice', 'burst'] },
  { path: 'theme.spark', label: 'Spark colour', group: 'General', kind: 'color', defaultValue: '#ffffff', variants: ['slice', 'burst'] },
  { path: 'theme.cornerRadius', label: 'Card corner radius', group: 'General', kind: 'range', min: 0, max: 40, step: 1, format: 'px', defaultValue: WEB_BASE.theme.cornerRadius, variants: ALL },
  // Gesture — slice
  // Pack — where the sealed pack sits before it opens (slice / burst; the
  // carousel lays its ring out on its own terms). Caps against the stage
  // left free by the host; centred, then nudged by the offset.
  { path: 'layout.pack.heightRatio', label: 'Pack size', group: 'Pack', kind: 'range', min: 0.5, max: 1, step: 0.01, format: 'ratio', defaultValue: WEB_BASE.layout.pack.heightRatio, variants: ['slice', 'burst'] },
  SLOT_PACK_SCALE_KNOB,
  { path: 'layout.pack.offsetY', label: 'Pack vertical offset', group: 'Pack', kind: 'range', min: -0.2, max: 0.2, step: 0.01, format: 'ratio', defaultValue: WEB_BASE.layout.pack.offsetY, variants: ['slice', 'burst'] },
  { path: 'interaction.activation', label: 'Swipe starts after', group: 'Gesture', kind: 'range', min: 0, max: 60, step: 1, format: 'px', defaultValue: 12, variants: SLICE },
  { path: 'interaction.completeFraction', label: 'Cut commits at', group: 'Gesture', kind: 'range', min: 0.2, max: 0.95, step: 0.01, format: 'ratio', defaultValue: 0.62, variants: SLICE },
  { path: 'theme.hint', label: 'Hint comet colour', group: 'Gesture', kind: 'color', defaultValue: '#ffffff', variants: SLICE },
  { path: 'hint.loopMs', label: 'Hint loop', group: 'Gesture', kind: 'range', min: 800, max: 4000, step: 50, format: 'ms', defaultValue: 1700, variants: SLICE },
  // Opening — slice
  { path: 'motion.open.ms', label: 'Lid off + card out', group: 'Opening', kind: 'range', min: 300, max: 2500, step: 50, format: 'ms', defaultValue: 1000, variants: SLICE },
  // Reveal — slice
  { path: 'slice.chargeMs', label: 'Card charge', group: 'Reveal', kind: 'range', min: 300, max: 4000, step: 50, format: 'ms', defaultValue: 1150, variants: SLICE },
  { path: 'slice.flipMs', label: 'Card flip', group: 'Reveal', kind: 'range', min: 200, max: 2000, step: 10, format: 'ms', defaultValue: 560, variants: SLICE },
  { path: 'slice.landMs', label: 'Card landing', group: 'Reveal', kind: 'range', min: 200, max: 2500, step: 10, format: 'ms', defaultValue: 780, variants: SLICE },
  // Carousel's preset holds 900 ms, slice's 600 ms; one path, one default
  { path: 'motion.reveal.holdMs', label: 'Hold before finish', group: 'Reveal', kind: 'range', min: 0, max: 3000, step: 50, format: 'ms', defaultValue: 900, variants: ['slice', 'carousel'] },
  { path: 'slice.embers', label: 'Embers', group: 'Reveal', kind: 'range', min: 0, max: 80, step: 1, format: 'count', defaultValue: 26, variants: SLICE },
  // The three glows around the card — the same card in every mechanic
  { path: 'motion.reveal.bloomAlpha', label: 'Bloom strength', group: 'Reveal', kind: 'range', min: 0, max: 1, step: 0.05, format: 'ratio', defaultValue: WEB_BASE.motion.reveal.bloomAlpha, variants: ALL },
  { path: 'motion.reveal.rimAlpha', label: 'Rim strength', group: 'Reveal', kind: 'range', min: 0, max: 1, step: 0.05, format: 'ratio', defaultValue: WEB_BASE.motion.reveal.rimAlpha, variants: ALL },
  { path: 'motion.reveal.haloAlpha', label: 'Halo strength', group: 'Reveal', kind: 'range', min: 0, max: 1, step: 0.05, format: 'ratio', defaultValue: WEB_BASE.motion.reveal.haloAlpha, variants: ALL },
  // The card's light phase by phase (phase names as onPhase reports them):
  // off leaves the card bare there, the glows and the beam on the outline
  // alike. Sleek's look wants the card at rest bare: the iframe draws the
  // Vault card's glow there, so the operator turns `hold` off
  { path: 'motion.reveal.glowDuring.open', label: 'Glow as the card comes out', group: 'Reveal', kind: 'toggle', defaultValue: true, variants: SLICE },
  { path: GLOW_WHILE_CHARGING, label: 'Glow while the card charges', group: 'Reveal', kind: 'toggle', defaultValue: true, variants: SLICE },
  { path: GLOW_WHILE_ART_LATE, label: 'Glow while the artwork is late', group: 'Reveal', kind: 'toggle', defaultValue: true, variants: SLICE },
  { path: 'motion.reveal.glowDuring.flip', label: 'Glow on the flip', group: 'Reveal', kind: 'toggle', defaultValue: true, variants: SLICE },
  { path: 'motion.reveal.glowDuring.land', label: 'Glow on landing', group: 'Reveal', kind: 'toggle', defaultValue: true, variants: SLICE },
  { path: 'motion.reveal.glowDuring.hold', label: 'Glow on the card at rest', group: 'Reveal', kind: 'toggle', defaultValue: true, variants: SLICE },
  { path: 'motion.reveal.glowFadeMs', label: 'Glow fade between phases', group: 'Reveal', kind: 'range', min: 0, max: 1000, step: 10, format: 'ms', defaultValue: 200, variants: SLICE },
  // Face down, the card's light takes a colour out of the list at every
  // corner the beam rounds, so it no longer gives the pull away before the
  // flip. It lives in the charge's glows: with those off there is nothing to see
  { path: CHARGE_CYCLE, label: 'Cycle colours before the flip', group: 'Reveal', kind: 'toggle', defaultValue: false, variants: SLICE, requires: [GLOW_WHILE_CHARGING, GLOW_WHILE_ART_LATE] },
  { path: 'motion.reveal.chargeCycleColors', label: 'Cycle colours', group: 'Reveal', kind: 'colorList', min: 1, max: CHARGE_CYCLE_MAX_COLORS, defaultValue: CHARGE_CYCLE_COLORS, variants: SLICE, requires: [CHARGE_CYCLE] },
  // Charge — burst
  { path: 'charge.holdMs', label: 'Hold to charge', group: 'Charge', kind: 'range', min: 300, max: 3000, step: 50, format: 'ms', defaultValue: 1100, variants: BURST },
  { path: 'charge.shake', label: 'Shudder', group: 'Charge', kind: 'range', min: 0, max: 0.03, step: 0.001, format: 'ratio', defaultValue: 0.009, variants: BURST },
  { path: 'charge.squeeze', label: 'Squeeze', group: 'Charge', kind: 'range', min: 0, max: 0.15, step: 0.005, format: 'ratio', defaultValue: 0.05, variants: BURST },
  // Blast — burst
  { path: 'burst.beatMs', label: 'Beat after the blast', group: 'Blast', kind: 'range', min: 0, max: 1500, step: 20, format: 'ms', defaultValue: 420, variants: BURST },
  { path: 'burst.cols', label: 'Shard columns', group: 'Blast', kind: 'range', min: 4, max: 24, step: 1, format: 'count', defaultValue: 13, variants: BURST },
  { path: 'burst.rows', label: 'Shard rows', group: 'Blast', kind: 'range', min: 6, max: 40, step: 1, format: 'count', defaultValue: 22, variants: BURST },
  { path: 'burst.shardMs', label: 'Shard flight', group: 'Blast', kind: 'range', min: 500, max: 3000, step: 50, format: 'ms', defaultValue: 1400, variants: BURST },
  { path: 'burst.screenFlashAlpha', label: 'Screen flash', group: 'Blast', kind: 'range', min: 0, max: 1, step: 0.05, format: 'ratio', defaultValue: 0.85, variants: BURST },
  { path: 'burst.kickAmp', label: 'Stage kick', group: 'Blast', kind: 'range', min: 0, max: 0.1, step: 0.005, format: 'ratio', defaultValue: 0.035, variants: BURST },
  { path: 'burst.glitter', label: 'Glitter', group: 'Blast', kind: 'range', min: 0, max: 200, step: 5, format: 'count', defaultValue: 70, variants: BURST },
  // Card arrival — burst
  { path: 'burst.assembleMs', label: 'Card assembly', group: 'Card arrival', kind: 'range', min: 400, max: 3000, step: 50, format: 'ms', defaultValue: 1400, variants: BURST },
  { path: 'burst.assembleStagger', label: 'Assembly stagger', group: 'Card arrival', kind: 'range', min: 0, max: 1, step: 0.05, format: 'ratio', defaultValue: 0.35, variants: BURST },
  // Ring — carousel
  { path: 'carousel.copies', label: 'Packs on the ring', group: 'Ring', kind: 'range', min: 3, max: 9, step: 1, format: 'count', defaultValue: 5, variants: CAROUSEL },
  { path: 'carousel.radius', label: 'Ring radius', group: 'Ring', kind: 'range', min: 0.7, max: 2.5, step: 0.05, format: 'x', defaultValue: 1.05, variants: CAROUSEL },
  { path: 'carousel.turn', label: 'Packs turn with the ring', group: 'Ring', kind: 'range', min: 0, max: 1.2, step: 0.01, format: 'ratio', defaultValue: 0.92, variants: CAROUSEL },
  { path: 'carousel.gather', label: 'Packs gathered to the front', group: 'Ring', kind: 'range', min: 0, max: 0.85, step: 0.01, format: 'ratio', defaultValue: 0.21, variants: CAROUSEL },
  { path: 'carousel.focal', label: 'Perspective (lens length)', group: 'Ring', kind: 'range', min: 1.5, max: 14, step: 0.1, format: 'x', defaultValue: 2.6, variants: CAROUSEL },
  { path: 'carousel.eye', label: 'Eye height', group: 'Ring', kind: 'range', min: 0, max: 2, step: 0.01, format: 'ratio', defaultValue: 0.34, variants: CAROUSEL },
  { path: 'carousel.shade', label: 'Packs shaded away from the front', group: 'Ring', kind: 'range', min: 0, max: 1, step: 0.01, format: 'ratio', defaultValue: 0.78, variants: CAROUSEL },
  { path: 'carousel.hoverAmp', label: 'Packs hover', group: 'Ring', kind: 'range', min: 0, max: 0.1, step: 0.002, format: 'ratio', defaultValue: 0.012, variants: CAROUSEL },
  { path: 'carousel.hoverMs', label: 'Hover beat', group: 'Ring', kind: 'range', min: 800, max: 12000, step: 100, format: 'ms', defaultValue: 8700, variants: CAROUSEL },
  { path: 'carousel.driftDps', label: 'Idle drift', group: 'Ring', kind: 'range', min: 0, max: 30, step: 0.5, format: 'count', defaultValue: 0, variants: CAROUSEL },
  { path: 'carousel.shuffleMs', label: 'Shuffle', group: 'Ring', kind: 'range', min: 400, max: 6000, step: 50, format: 'ms', defaultValue: 2600, variants: CAROUSEL },
  { path: 'carousel.shuffleTurns', label: 'Shuffle turns', group: 'Ring', kind: 'range', min: 0, max: 4, step: 1, format: 'count', defaultValue: 2, variants: CAROUSEL },
  { path: 'carousel.dragTurn', label: 'Drag turns per pack width', group: 'Ring', kind: 'range', min: 0.05, max: 0.6, step: 0.01, format: 'ratio', defaultValue: 0.2, variants: CAROUSEL },
  { path: 'carousel.settleMs', label: 'Settle after a drag', group: 'Ring', kind: 'range', min: 100, max: 2000, step: 10, format: 'ms', defaultValue: 520, variants: CAROUSEL },
  { path: 'carousel.riseScale', label: 'Chosen pack grows to', group: 'Ring', kind: 'range', min: 1, max: 3, step: 0.05, format: 'x', defaultValue: 1.15, variants: CAROUSEL },
  { path: 'carousel.reflectionAlpha', label: 'Floor reflection', group: 'Ring', kind: 'range', min: 0, max: 1, step: 0.02, format: 'ratio', defaultValue: 0.32, variants: CAROUSEL },
  { path: 'carousel.reflectionGap', label: 'Gap to the reflection', group: 'Ring', kind: 'range', min: 0, max: 0.2, step: 0.005, format: 'ratio', defaultValue: 0.04, variants: CAROUSEL },
  // Story — carousel
  { path: 'carousel.dissolveMs', label: 'Pack dissolves', group: 'Story', kind: 'range', min: 100, max: 1500, step: 10, format: 'ms', defaultValue: 380, variants: CAROUSEL },
  { path: 'carousel.factGapMs', label: 'Pause between facts', group: 'Story', kind: 'range', min: 0, max: 2000, step: 10, format: 'ms', defaultValue: 700, variants: CAROUSEL },
  { path: 'carousel.bannerHoldMs', label: 'Banner holds', group: 'Story', kind: 'range', min: 0, max: 4000, step: 10, format: 'ms', defaultValue: 1500, variants: CAROUSEL },
  { path: 'carousel.bannerTilt', label: 'Banner tilt', group: 'Story', kind: 'range', min: -30, max: 30, step: 1, format: 'count', defaultValue: -12, variants: CAROUSEL },
  { path: 'carousel.flipMs', label: 'Card flip', group: 'Story', kind: 'range', min: 200, max: 2000, step: 10, format: 'ms', defaultValue: 520, variants: CAROUSEL },
];

/** Knobs the given mechanic reads — in `template`, when given. */
export function knobsFor(variant: AnimationVariant, template?: PreviewTemplate): Knob[] {
  return ANIMATION_KNOBS.filter(
    (knob) =>
      knob.variants.includes(variant) &&
      (template === undefined || knob.templates === undefined || knob.templates.includes(template)),
  );
}

type Plain = Record<string, unknown>;

const isPlain = (value: unknown): value is Plain =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Reads `path` ("a.b.c") out of a nested plain object; undefined when absent. */
export function getPath(source: unknown, path: string): unknown {
  let node: unknown = source;
  for (const key of path.split('.')) {
    if (!isPlain(node)) return undefined;
    node = node[key];
  }
  return node;
}

/**
 * Returns a copy of `source` with `path` set to `value`, creating intermediate
 * objects. Never mutates the input — reducer state relies on that.
 */
export function setPath<T extends Plain>(source: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const assign = (node: Plain, index: number): Plain => {
    const key = keys[index]!;
    if (index === keys.length - 1) return { ...node, [key]: value };
    const child = node[key];
    return { ...node, [key]: assign(isPlain(child) ? child : {}, index + 1) };
  };
  return assign(source, 0) as T;
}

/** The slider readout: "2600 ms", "12 px", "1.50×", "62%", "26". */
export function formatKnobValue(knob: RangeKnob, value: number): string {
  switch (knob.format) {
    case 'ms':
      return `${Math.round(value)} ms`;
    case 'px':
      return `${Math.round(value)} px`;
    case 'x':
      return `${value.toFixed(2)}×`;
    case 'ratio': {
      // Tiny ranges (shudder, kick) need a decimal to move at all
      const digits = knob.max < 0.2 ? 1 : 0;
      return `${(value * 100).toFixed(digits)}%`;
    }
    case 'count':
      return String(Math.round(value));
  }
}
